import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { onIdTokenChanged, type User } from 'firebase/auth';
import { getAuthInstance } from './firebase';
import { ensureUserProfile } from './userProgress';

interface AuthContextValue {
  user: User | null;
  /** Always false now that anonymous auth is removed; kept for callsite stability. */
  isAnonymous: boolean;
  isReady: boolean;
  /** Resolves once the initial auth state is known. Value may be null (guest). */
  ready: Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface InternalState {
  user: User | null;
  isReady: boolean;
  // Bumped on every token change so React always re-renders even if the User
  // instance reference is identical (happens after linkWithCredential).
  tick: number;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<InternalState>({
    user: null,
    isReady: false,
    tick: 0,
  });

  const resolveReadyRef = useRef<((u: User | null) => void) | null>(null);
  const readyResolvedRef = useRef(false);
  const readyRef = useRef<Promise<User | null>>(
    new Promise<User | null>((resolve) => {
      resolveReadyRef.current = resolve;
    }),
  );
  const ensureProfileFor = useRef<Set<string>>(new Set());

  useEffect(() => {
    const auth = getAuthInstance();
    let cancelled = false;

    const unsubscribe = onIdTokenChanged(auth, (u) => {
      if (cancelled) return;
      setState((prev) => ({ user: u, isReady: true, tick: prev.tick + 1 }));
      if (!readyResolvedRef.current) {
        readyResolvedRef.current = true;
        resolveReadyRef.current?.(u);
      }
      if (u) {
        const profileKey = u.uid;
        if (!ensureProfileFor.current.has(profileKey)) {
          ensureProfileFor.current.add(profileKey);
          ensureUserProfile(u).catch(() => {
            // ignore profile write errors (e.g. offline)
          });
        }
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const value: AuthContextValue = {
    user: state.user,
    isAnonymous: false,
    isReady: state.isReady,
    ready: readyRef.current,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}

/** Hook returning the current user once auth is ready. Returns null during bootstrap. */
export function useCurrentUser(): User | null {
  const { user, isReady } = useAuth();
  return isReady ? user : null;
}

/** Subscribe-style helper for non-React contexts (returns unsubscribe). */
export function subscribeAuth(cb: (user: User | null) => void): () => void {
  return onIdTokenChanged(getAuthInstance(), cb);
}
